import React, { useState, useEffect } from 'react'

import ReportService from '../../services/ReportService'
import LocalStorageService from '../../services/LocalStorageService'


// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Appt Components
import Anchor from '../../components/simple/anchor/Anchor'
import Box from '../../components/simple/box/Box'

import FormField from '../../components/simple/formField/FormField.js'
import SmartPlanSearchResults from './SmartPlanSearchResults.js'
import Text from '../../components/simple/text/Text'

import FieldBox from './shared/FieldBox.js'
import JoinBox from './shared/JoinBox.js'
import OperatorBox from './shared/OperatorBox.js'
import ValueBox from './shared/ValueBox.js'

// Shared Components
import BreadcrumbBar from '../shared/BreadcrumbBar'
import PlanLinkBar from './shared/PlanLinkBar.js'
import ActionButton from '../../components/compound/actionButton/ActionButton.js'

import PulseLoader from 'react-spinners/PulseLoader.js'


function SmartPlans(props) {

  const apiToken = LocalStorageService.get('apiToken')
  const activeOrg = LocalStorageService.get('activeOrg')

  const [criteriaIndex, setCriteriaIndex] = useState(0)

  const [fields, setFields] = useState([])

  const [query, setQuery] = useState()
  const [queryResults, setQueryResults] = useState()
  const [loading, setLoading] = useState(false)

  // If a different field has been selected then
  // set the field type of the field selected
  // so that the Operator dropdown can be
  // populated accordingly
  const handleFieldChanged = (fieldName, value) => {
    const fieldIndex = fieldName.substring(fieldName.indexOf('-') + 1)
    const newFields = fields

    newFields[fieldIndex] = value

    setFields(newFields)
  }

  // Delete a Criteria row
  const removeCriteria = (index) => {
    // Remove the query
    if (query?.[`Field-${index}`]) {
      delete query[`Field-${index}`]
    }

    if (query?.[`Query-${index}`]) {
      delete query[`Query-${index}`]
    }

    if (query?.[`Value-${index}`]) {
      delete query[`Value-${index}`]
    }

    if (query?.[`Join-${index}`]) {
      delete query[`Join-${index}`]
    }

    // Rename the remaining query keys
    // eg. Field-1 -> Field-0
    //
    // if current key > index decrement by 1
    const newQuery = {}

    for (const item in query) {
      const fieldName = item.split('-')[0]
      let fieldIndex = item.split('-')[1]
      const fieldValue = query[fieldName.concat('-', fieldIndex)]

      if (Number.parseInt(fieldIndex) > index) {
        fieldIndex--
      }

      newQuery[fieldName.concat('-', fieldIndex)] = fieldValue
    }

    // Update new values
    const newFields = fields

    newFields.splice(index, 1)
    setFields(newFields)

    setQuery(newQuery)
    setCriteriaIndex(criteriaIndex - 1)
    buildCriteria()
  }

  // When Field changes default the Query box to the first option
  const setDefaultOperatorValue = (defautValue, index) => {
    if (query && query[`Field-${index}`]) {
      query[`Query-${index}`] = defautValue
    }
  }

  const criteriaRow = (index, addJoin) => {
    return (
      <>
        <Box direction='row-responsive' gap='medium'>
          <FormField
            direction='column'
            label='Field'
            name={`Field-${index}`}
          >
            <FieldBox
              handleFieldChanged={handleFieldChanged}
              name={`Field-${index}`}
            />
          </FormField>

          <FormField
            direction='column'
            label='Query'
            name={`Query-${index}`}
          >
            <OperatorBox
              index={index}
              name={`Query-${index}`}
              type={fields[index]?.type}
              operators={fields[index]?.operators}
              setDefaultOperatorValue={setDefaultOperatorValue}
              fieldType={fields[index]?.value}
              query={query}
            />
          </FormField>

          <FormField
            direction='column'
            label='Value'
            name={`Value-${index}`}
          >
            <ValueBox
              name={`Value-${index}`}
              type={fields[index]?.type}
              list={fields[index]?.list}
              fieldType={fields[index]?.value}
              index={index}
              query={query}
            />
          </FormField>

          <ActionButton label={<Text><FontAwesomeIcon icon={['fal', 'minus-circle']} /></Text>} margin={{ horizontal: 'xsmall', vertical: 'medium' }} style={{ background: 'besmartOrange' }} onClick={() => removeCriteria(index)} target='_self' />
        </Box>

        {addJoin &&
          <Box direction='row-responsive' gap='medium'>
            <FormField
              direction='column'
              label='Join'
              name={`Join-${index}`}
            >
              <JoinBox
                name={`Join-${index}`}
              />
            </FormField>
          </Box>}
      </>

    )
  }

  const buildCriteria = () => {
    const criteriaTable = []

    for (let i = 0; i <= criteriaIndex; i++) {
      criteriaTable.push(criteriaRow(i, (criteriaIndex > 0 && i !== criteriaIndex)))
    }

    return criteriaTable
  }

  const buildQuery = async () => {
    const queryArray = []

    await getResults(queryArray)

    return queryArray
  }

  const getResults = async (queryArray) => {
    if (queryArray) {
      const body = {
        submissionType: 'behaviourplan',
        query: queryArray,
        reports: ['globalIncidentSearch']
      }

      const params = {
        orgId: activeOrg.id
      }

      setLoading(true)
      const results = await ReportService.getReport(apiToken, params, body)
      setLoading(false)

      if (results?.incidentSearch) {
        setQueryResults(results.incidentSearch)
      }

      return results?.incidentSearch
    }
  }

  useEffect(() => {
    void buildQuery()
  }, [])

  useEffect(() => {
    buildCriteria()
  }, [fields, query])

  return (
    <Box width='xlarge'>
      <BreadcrumbBar
        title="My Smart Plans"
        path={<><Anchor href='/'>BehaviourSmart</Anchor><Text color='besmartBlue' size='xsmall'>{window.location.pathname}</Text></>}
      />

      <PlanLinkBar />
      <Box background='white' direction='column' gap='small' round='small'>
        <Box
          gap='small'
          margin={{ horizontal: 'small' }}
          pad={{ horizontal: 'small', bottom: 'medium' }}
          round='small'
        >
          <Box direction='column' gap='small'>
            <Box direction='column' gap='xsmall' margin={{ top: 'small' }}>
              <Text margin={{ top: 'small', bottom: 'small' }} size='xlarge'>Results</Text>

              {queryResults?.length > 0 &&
                <SmartPlanSearchResults
                  results={queryResults}
                  type='smartplan'
                  terms={props.terms}
                />}
              {!loading && !queryResults?.length > 0 && <Text color='#666'>- No results to display -</Text>}
              <PulseLoader size={15} color='#ff8300' loading={loading} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default SmartPlans
